import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import SheinOpsPager, { SHEIN_OPS_DEFAULT_PAGE_SIZE } from '@/shein-platform/components/SheinOpsPager';
import { togglePageSelection } from '@/shein-platform/utils/sheinPageSelection';
import SheinOpsListToolbar, { downloadCsv } from '@/shein-platform/components/SheinOpsListToolbar';
import SheinSyncProgressSlot from '@/shein-platform/components/SheinSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/shein-platform/utils/buildOpsExportMenu';
import { fetchAllPages } from '@/shein-platform/utils/sheinExportHelpers';
import { ProductFilterCard, ProductListCard } from '@/shein-platform/modules/products/components/ProductPageCards';
import { useGuardedRefresh } from '@/shein-platform/hooks/useGuardedRefresh';
import { useSheinPageShopScope } from '@/shein-platform/hooks/useSheinPageShopScope';
import { useSheinSyncJobs } from '@/shein-platform/context/SheinSyncJobContext';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import { extractSheinList, pickField } from '@/shein-platform/utils/sheinResponse';
import {
  agencyApplyStatusLabel,
  agencyCoveredRangeLabel,
  agencyOriginTypeLabel,
  agencyStatusLabel,
  agencySubTypeLabel,
  agencyTypeLabel,
  formatAgencyContact,
  formatAgencyValidity,
} from '@/shein-platform/modules/compliance/complianceDisplay';
import { formatSiteListSyncedAt } from '@/shein-platform/utils/sheinSiteList';
import { showToast } from '@/utils/toast';
import '@/styles/shein-platform/shein-product-ops.css';

const TABLE_COLUMNS = [
  { key: 'check', label: '', width: '50px' },
  { key: 'name', labelKey: 'compliance.colName' },
  { key: 'type', labelKey: 'compliance.colType' },
  { key: 'contact', labelKey: 'compliance.colContact' },
  { key: 'validity', labelKey: 'compliance.colValidity' },
  { key: 'applyStatus', labelKey: 'compliance.colApply' },
  { key: 'scope', labelKey: 'compliance.colScope' },
  { key: 'ops', labelKey: 'certLib.colOps', width: '88px' },
];

const COL_SPAN = TABLE_COLUMNS.length;

function displayValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join('、') : '—';
  return String(value);
}

function rowKey(row, index) {
  return String(pickField(row, 'agencyId', 'id', 'companyId') || `agency-${index}`);
}

function applyStatusModifier(row) {
  const code = Number(pickField(row, 'applyStatus'));
  if (code === 2) return 'success';
  if (code === 3 || code === 0) return 'failed';
  return 'pending';
}

function KvGrid({ rows }) {
  const { t } = useTranslation('workbench');
  if (!rows.length) {
    return <p className="spo-return-detail__empty">{t('freightLib.noData')}</p>;
  }
  return (
    <table className="spo-return-detail__kv">
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <th scope="row">{row.label}</th>
            <td>
              {row.href ? (
                <a href={row.href} target="_blank" rel="noreferrer">
                  {displayValue(row.value)}
                </a>
              ) : (
                displayValue(row.value)
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AgencyDetailModal({ open, row, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const sections = useMemo(() => {
    if (!row) return [];
    const agreementUrl = pickField(row, 'agencyAgreementUrl');
    const agreementName = pickField(row, 'agencyAgreementName') || agreementUrl || t('compliance.viewAgreement');
    return [
      {
        title: t('compliance.basic'),
        rows: [
          { label: t('compliance.companyName'), value: pickField(row, 'agencyName', 'companyName', 'name') },
          { label: t('compliance.companyId'), value: pickField(row, 'agencyId', 'id', 'companyId') },
          { label: t('compliance.dutyName'), value: pickField(row, 'title') },
          { label: t('compliance.colType'), value: agencyTypeLabel(pickField(row, 'agencyType')) },
          { label: t('compliance.subType'), value: agencySubTypeLabel(pickField(row, 'agencySubType')) },
          { label: t('compliance.colApply'), value: agencyApplyStatusLabel(pickField(row, 'applyStatus')) },
          { label: t('compliance.companyStatus'), value: agencyStatusLabel(pickField(row, 'agencyStatus')) },
          { label: t('compliance.colScope'), value: agencyCoveredRangeLabel(pickField(row, 'coveredProductRange')) },
          { label: t('compliance.origin'), value: agencyOriginTypeLabel(pickField(row, 'originType')) },
          { label: t('compliance.supplierId'), value: pickField(row, 'supplierId') },
          { label: t('compliance.brandCode'), value: row.brandCodes },
        ],
      },
      {
        title: t('compliance.contactTitle'),
        rows: [
          { label: t('compliance.contact'), value: pickField(row, 'contactName') },
          { label: t('compliance.areaCode'), value: pickField(row, 'areaCode') },
          { label: t('compliance.phone'), value: pickField(row, 'telephone') },
          { label: t('compliance.email'), value: pickField(row, 'email') },
        ],
      },
      {
        title: t('compliance.addressTitle'),
        rows: [
          { label: t('compliance.country'), value: pickField(row, 'agencyCountryName') },
          { label: t('compliance.province'), value: pickField(row, 'agencyProvinceName') },
          { label: t('compliance.city'), value: pickField(row, 'agencyCityName') },
          { label: t('compliance.address'), value: pickField(row, 'agencyDetailAddress') },
          { label: t('compliance.postal'), value: pickField(row, 'postalCode') },
        ],
      },
      {
        title: t('compliance.agreementTitle'),
        rows: [
          {
            label: t('compliance.agreement'),
            value: agreementName,
            href: agreementUrl || undefined,
          },
          { label: t('compliance.agreeStart'), value: pickField(row, 'agencyStartTime') },
          { label: t('compliance.agreeEnd'), value: pickField(row, 'agencyEndTime') },
          { label: t('productTemplate.colCreatedAt'), value: pickField(row, 'createTime') },
          { label: t('productTemplate.colUpdatedAt'), value: pickField(row, 'updateTime') },
          { label: t('compliance.trace'), value: pickField(row, 'traceId') },
        ],
      },
    ];
  }, [row, t]);

  if (!open || !row) return null;

  const name = pickField(row, 'agencyName', 'companyName', 'name') || '—';
  const id = pickField(row, 'agencyId', 'id', 'companyId') || '—';

  return createPortal(
    <div className="spo-batch-modal spo-cert-modal" role="presentation">
      <button type="button" className="spo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="spo-batch-modal__panel spo-cert-modal__panel spo-agency-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-agency-detail-title"
      >
        <div className="spo-batch-modal__head">
          <div>
            <h2 id="spo-agency-detail-title">{t('compliance.detailTitle')}</h2>
            <div className="spo-return-detail__meta" style={{ marginTop: 6 }}>
              <strong className="spo-return-detail__no">{name}</strong>
              <span className="spo-muted">ID {id}</span>
            </div>
          </div>
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="spo-batch-modal__body spo-cert-modal__body spo-return-detail__body">
          {sections.map((section) => (
            <section key={section.title} className="spo-return-detail__section">
              <h3 className="spo-return-detail__section-title">{section.title}</h3>
              <KvGrid rows={section.rows} />
            </section>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * 合规资料 · 公司代理：
 * 列表读本地缓存表；「拉取 SHEIN 后台数据」拉平台全量写入 shein_compliance_agency_cache。
 */
export default function AgencyCompaniesPanel({
  syncKinds = ['agency'],
} = {}) {
  const { t } = useTranslation('workbench');
  const {
    activeShop,
    activeShopId,
    concreteScopeId,
    allShopsScope,
    shopDimensionTabs,
  } = useSheinPageShopScope();
  const shopId = concreteScopeId || (allShopsScope ? '' : (activeShop?.id || activeShopId));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SHEIN_OPS_DEFAULT_PAGE_SIZE);
  const [filterDraft, setFilterDraft] = useState({ companyId: '', companyName: '' });
  const [query, setQuery] = useState({ companyId: '', companyName: '' });
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [detailRow, setDetailRow] = useState(null);
  const { startJob, onTrackDone, isBusy } = useSheinSyncJobs();

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [shopId, query]);

  const buildParams = useCallback((pageNo, size, q) => {
    const params = {
      pageNum: pageNo,
      pageSize: Math.min(100, Math.max(1, size)),
      bindableOnly: false,
    };
    const idRaw = String(q?.companyId || '').trim();
    if (idRaw && /^\d+$/.test(idRaw)) {
      params.agencyId = idRaw;
    }
    const name = String(q?.companyName || '').trim();
    if (name) {
      params.agencyName = name;
    }
    return params;
  }, []);

  const fetchList = useCallback(async ({ pageNo = page, size = pageSize, q = query } = {}) => {
    if (!shopId) {
      setRows([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return { list: [], total: 0 };
    }
    setLoading(true);
    try {
      const res = await sheinPlatformAPI.listComplianceAgencies(shopId, buildParams(pageNo, size, q));
      const list = extractSheinList(res);
      const totalFromCache = Number(res?.total);
      if (Number.isFinite(totalFromCache) && totalFromCache >= 0) {
        setRows(list);
        setTotal(totalFromCache);
        return { list, total: totalFromCache };
      }
      const pageCount = Number(res?.count);
      const counted = Number.isFinite(pageCount) ? pageCount : list.length;
      const nextTotal = counted < size
        ? (pageNo - 1) * size + counted
        : pageNo * size + 1;
      setRows(list);
      setTotal(nextTotal);
      // 跨页选中：翻页刷新列表时保留已选
      return { list, total: nextTotal };
    } catch (e) {
      setRows([]);
      setTotal(0);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [shopId, page, pageSize, query, buildParams]);

  useEffect(() => {
    if (!shopId) {
      setRows([]);
      setTotal(0);
      return;
    }
    fetchList({ pageNo: page, size: pageSize, q: query }).catch((e) => {
      showToast(e?.message || t('compliance.loadAgencyFailed'), 'error');
    });
  }, [shopId, page, pageSize, query, fetchList, t]);

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
      return;
    }
    try {
      const track = await startJob({
        kind: 'agency',
        shopId,
        label: t('publishRecord.syncUpdate'),
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      await fetchList({ pageNo: page, size: pageSize, q: query });
      setLastSyncedAt(new Date().toISOString());
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      showToast(e?.message || t('productAction.syncFailed'), 'error');
    }
  }, [shopId, startJob, onTrackDone, fetchList, page, pageSize, query, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('compliance', shopId);
  const latestSyncedLabel = lastSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(lastSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index)),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onQuery = () => {
    setQuery({
      companyId: String(filterDraft.companyId || '').trim(),
      companyName: String(filterDraft.companyName || '').trim(),
    });
    setPage(1);
  };

  const onReset = () => {
    setFilterDraft({ companyId: '', companyName: '' });
    setQuery({ companyId: '', companyName: '' });
    setPage(1);
  };

  const writeAgencyCsv = (exportRows, filename) => {
    if (!exportRows.length) {
      showToast(t('compliance.noneAgency'), 'warning');
      return;
    }
    downloadCsv(
      filename,
      ['agencyId', 'agencyName', 'agencyType', 'contact', 'validity', 'applyStatus', 'coveredRange'],
      exportRows.map((row) => ({
        agencyId: pickField(row, 'agencyId', 'id', 'companyId') || '',
        agencyName: pickField(row, 'agencyName', 'companyName', 'name') || '',
        agencyType: agencyTypeLabel(pickField(row, 'agencyType')),
        contact: formatAgencyContact(row),
        validity: formatAgencyValidity(row),
        applyStatus: agencyApplyStatusLabel(pickField(row, 'applyStatus')),
        coveredRange: agencyCoveredRangeLabel(pickField(row, 'coveredProductRange')),
      })),
    );
    showToast(t('compliance.exportedAgency', { count: exportRows.length }), 'success');
  };

  const exportFiltered = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(async (pageNo, size) => {
        const res = await sheinPlatformAPI.listComplianceAgencies(shopId, buildParams(pageNo, size, query));
        const list = extractSheinList(res);
        const totalFromCache = Number(res?.total);
        if (Number.isFinite(totalFromCache) && totalFromCache >= 0) {
          return { items: list, total: totalFromCache };
        }
        const pageCount = Number(res?.count);
        const counted = Number.isFinite(pageCount) ? pageCount : list.length;
        const nextTotal = counted < size
          ? (pageNo - 1) * size + counted
          : pageNo * size + 1;
        return { items: list, total: nextTotal };
      });
      writeAgencyCsv(all, `shein-agency-companies-filtered-${Date.now()}.csv`);
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const exportPage = () => {
    writeAgencyCsv(rows, `shein-agency-companies-page-${Date.now()}.csv`);
  };

  const exportSelected = () => {
    const selected = rows.filter((row, index) => selectedKeys.has(rowKey(row, index)));
    if (!selected.length) {
      showToast(t('compliance.pickExportAgency'), 'warning');
      return;
    }
    writeAgencyCsv(selected, `shein-agency-companies-selected-${Date.now()}.csv`);
  };

  return (
    <>
      <SheinSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <SheinOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || exporting}
            syncKinds={syncKinds}
            quotaKind="agency"
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'SHEIN' }), 'warning');
                return;
              }
              guardedSync();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('compliance.agencySyncHint', { platform: 'SHEIN' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
          />
        )}
      >
        <div className="spo-filter-bar">
          <div className="spo-filter-bar__fields">
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{t('compliance.companyIdLabel')}</span>
              <input
                className="spo-input"
                value={filterDraft.companyId}
                placeholder={t('compliance.idPh')}
                disabled={!shopId || syncing}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, companyId: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
            <div className="spo-field spo-field--inline">
              <span className="spo-field__label">{t('compliance.companyName')}</span>
              <input
                className="spo-input"
                value={filterDraft.companyName}
                placeholder={t('compliance.namePh')}
                disabled={!shopId || syncing}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, companyName: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
            <button
              type="button"
              className="spo-btn spo-btn--primary spo-btn--sm"
              disabled={!shopId || syncing}
              onClick={onQuery}
            >
              {t('certList.search')}
            </button>
            <button
              type="button"
              className="spo-btn spo-btn--secondary spo-btn--sm"
              disabled={!shopId || syncing}
              onClick={onReset}
            >
              {t('returnList.reset')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard tabs={shopDimensionTabs} loading={Boolean(shopId) && loading} loadingLabel={t('compliance.syncingAgency')}>
        <div className="spo-drafts__table-wrap spo-certs-table-wrap">
          <table className="spo-publish__table spo-drafts__table spo-certs-table">
            <colgroup>
              {TABLE_COLUMNS.map((col) => (
                <col
                  key={col.key}
                  className={`spo-certs-col spo-certs-col--${col.key}`}
                  style={col.width ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="spo-certs-th spo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('compliance.selectAllAgency')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {TABLE_COLUMNS.filter((c) => c.key !== 'check').map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`spo-certs-th spo-certs-th--${col.key}`}
                  >
                    {col.labelKey ? t(col.labelKey) : col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!shopId ? (
                <tr>
                  <td colSpan={COL_SPAN} className="spo-drafts__empty-cell">
                    {allShopsScope ? t('draftBox.pickConcrete') : t('compliance.pickAuthorizedShort', { platform: 'SHEIN' })}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_SPAN} className="spo-drafts__empty-cell">
                    {lastSyncedAt ? t('freightLib.noData') : t('compliance.emptyAgency', { platform: 'SHEIN' })}
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = rowKey(row, index);
                  const id = pickField(row, 'agencyId', 'id', 'companyId') || '—';
                  const name = pickField(row, 'agencyName', 'companyName', 'name') || '—';
                  return (
                    <tr key={key}>
                      <td
                        className="spo-certs-td spo-certs-td--check"
                        onClick={() => toggleOne(key)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('orderList.selectRow', { title: name })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="spo-certs-td">
                        <div>{name}</div>
                        <div className="spo-muted" style={{ fontSize: 12 }}>{id}</div>
                      </td>
                      <td className="spo-certs-td">{agencyTypeLabel(pickField(row, 'agencyType'))}</td>
                      <td className="spo-certs-td">{formatAgencyContact(row)}</td>
                      <td className="spo-certs-td">{formatAgencyValidity(row)}</td>
                      <td className="spo-certs-td">
                        <span className={`spo-records__status spo-records__status--${applyStatusModifier(row)}`}>
                          {agencyApplyStatusLabel(pickField(row, 'applyStatus'))}
                        </span>
                      </td>
                      <td className="spo-certs-td">
                        {agencyCoveredRangeLabel(pickField(row, 'coveredProductRange'))}
                      </td>
                      <td className="spo-certs-td spo-certs-td--ops">
                        <button
                          type="button"
                          className="spo-btn spo-btn--ghost spo-btn--sm"
                          onClick={() => setDetailRow(row)}
                        >
                          {t('orderList.detail')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <SheinOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(Math.min(100, size));
            setPage(1);
          }}
          pageSizeOptions={[30, 50, 100]}
          ariaLabel={t('compliance.agencyPager')}
        />
      </ProductListCard>

      <AgencyDetailModal
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
      />
    </>
  );
}
